/**
 * Modern entities service using JSONL + Parquet architecture
 * High-performance entity loading with HTTP Range requests
 */

import { formatInput } from '../utils/searchUtils.js';
import { OVERSEAS_TERRITORIES } from '../config/settings.js';
import { normalizeRiskId } from '../utils/risksMapping.js';

/**
 * Cache for the loaded Parquet index
 * @type {Array|null}
 */
let parquetIndexCache = null;

/**
 * Promise for loading the index (to avoid multiple simultaneous loads)
 * @type {Promise|null}
 */
let indexLoadingPromise = null;

/**
 * Configuration for the entities service
 */
export const ENTITIES_CONFIG = {
  staleTime: 1000 * 60 * 30, // 30 minutes
  gcTime: 1000 * 60 * 60 * 2, // 2 hours
  jsonlUrl: '/entities.jsonl',
  parquetIndexUrl: '/entities-index.parquet',
};

/**
 * Load Parquet index using Hyparquet library
 * @async
 * @returns {Promise<Array>} Array of index records with offset/length data
 */
// eslint-disable-next-line require-await
const loadParquetIndex = async () => {
  // Return cached index if available
  if (parquetIndexCache) {
    return parquetIndexCache;
  }

  // If already loading, return the existing promise
  if (indexLoadingPromise) {
    return indexLoadingPromise;
  }

  // Start loading
  indexLoadingPromise = (async () => {
    try {
      // Import Hyparquet dynamically
      const { parquetRead } = await import('hyparquet');

      // Fetch the Parquet file
      const response = await fetch(ENTITIES_CONFIG.parquetIndexUrl);
      if (!response.ok) {
        throw new Error(`Failed to load Parquet index: ${response.status} ${response.statusText}`);
      }

      const buffer = await response.arrayBuffer();

      // Read Parquet data
      const indexData = [];
      await parquetRead({
        file: buffer,
        onComplete: (result) => {
          if (result && Array.isArray(result)) {
            indexData.push(...result);
          }
        }
      });

      // Convert array format to object format for easier access
      const formattedIndex = indexData.map((row) => ({
        identifiant: row[0],
        offset: Number(row[1]), // Convert BigInt to number for Range requests
        length: Number(row[2]),
        nom: row[3],
        type: row[4],
        code_postal: row[5],
      }));

      // Cache the result
      parquetIndexCache = formattedIndex;

      // Reset loading promise
      indexLoadingPromise = null;

      return formattedIndex;

    } catch (error) {
      indexLoadingPromise = null; // Reset on error to allow retry
      throw new Error(`Parquet index loading failed: ${error.message}`, { cause: error });
    }
  })();

  return indexLoadingPromise;
};

/**
 * Find entity in the Parquet index
 * @param {string} insee - INSEE code to find
 * @returns {Promise<Object|null>} Index record with offset/length or null if not found
 */
const findEntityInIndex = async (insee) => {
  const index = await loadParquetIndex();
  return index.find((record) => record.identifiant === insee) || null;
};

/**
 * Fetch entity data from JSONL using HTTP Range request
 * @param {string} insee - INSEE code of the entity
 * @returns {Promise<Object>} Entity data
 */
const fetchEntityFromJsonl = async (insee) => {
  if (!insee) {
    throw new Error('INSEE code is required');
  }

  // Find entity in index
  const indexRecord = await findEntityInIndex(insee);
  if (!indexRecord) {
    throw new Error(`Entity with INSEE code ${insee} not found in index`);
  }

  // Use HTTP Range request to fetch only the needed bytes
  const response = await fetch(ENTITIES_CONFIG.jsonlUrl, {
    headers: {
      'Range': `bytes=${indexRecord.offset}-${indexRecord.offset + indexRecord.length - 1}`
    }
  });

  if (!response.ok && response.status !== 206) {
    throw new Error(`Range request failed: ${response.status} ${response.statusText}`);
  }

  // Parse the JSON line
  const jsonLine = await response.text();
  const entity = JSON.parse(jsonLine.trim());

  // Validate entity
  if (!entity.identifiant_insee || entity.identifiant_insee !== insee) {
    throw new Error(`Entity data inconsistency: expected ${insee}, got ${entity.identifiant_insee}`);
  }

  return entity;
};

/**
 * Get entities index for search functionality
 * Returns only the index data (lightweight) for search, not full entity data
 *
 * `searchName` and `searchWords` are normalized once here rather than on every
 * keystroke: re-running formatInput over the whole index per keypress costs
 * ~65ms, which blows the frame budget on every character typed.
 *
 * They are deliberately NOT precomputed into entities-index.parquet by
 * scripts/generate-storage-files.js, even though that is where derived data
 * would normally belong. Measured on the 36k-entity index:
 *
 *   - normalizing here, once:      ~28ms of CPU, 0 bytes over the wire
 *   - a `search_name` parquet column: +269KB brotli, +14ms to decode
 *
 * The column is pure redundancy (it is a transformation of `nom`, already
 * present), parquet cannot compress that away, and it only saves ~23ms since
 * parquet stores no arrays — `searchWords` would still need splitting on load.
 * Trading 269KB of critical-path download for 23ms of CPU is a bad deal, and a
 * worse one on mobile, where both the decode and the transfer degrade.
 * @returns {Promise<Array>} Array of index records for search
 */
const getEntitiesIndex = async () => {
  const index = await loadParquetIndex();

  // Convert to search format compatible with existing search functions
  return index.map((record) => {
    const searchName = formatInput(record.nom || '');

    return {
      identifiant: record.identifiant,
      code: record.code_postal,
      nom: record.nom,
      type: record.type,
      label: `${record.nom} (${record.code_postal})`,
      searchName,
      // Split on the original separators: formatInput turns '-' and "'" into
      // spaces, so runs of them ("Sarroux - Saint Julien") would yield empty words.
      searchWords: searchName.split(/[\s-]+/),
      // Keep index metadata for potential future lazy loading
      _offset: record.offset,
      _length: record.length,
    };
  });
};

/**
 * Enrich entity data with additional computed properties
 * @param {Object} entity - The raw entity data
 * @returns {Object} The enriched entity data
 */
const enrichEntity = (entity) => {
  if (!entity) return entity;

  const enrichedEntity = { ...entity };

  // Add 'om' property for communes in Outre-Mer departments
  if (entity.type === 'commune' && entity.nom_departement) {
    enrichedEntity.om = OVERSEAS_TERRITORIES.isDepartmentOverseas(entity.nom_departement);
  }

  // Normalize territory-suffixed risk identifiers (e.g., cycloneNC → cyclone)
  if (Array.isArray(entity.risques)) {
    enrichedEntity.risques = entity.risques.map(normalizeRiskId);
  }

  return enrichedEntity;
};

/**
 * Fetch a single entity by its INSEE code using modern JSONL+Parquet architecture
 * @param {string} insee - INSEE code of the entity
 * @returns {Promise<Object>} Entity data
 */
export const fetchEntity = async (insee) => {
  if (!insee) {
    throw new Error('INSEE code is required');
  }

  const entity = await fetchEntityFromJsonl(insee);
  return enrichEntity(entity);
};

/**
 * Alias for fetchEntity (for compatibility with hook naming)
 * @param {string} insee - INSEE code of the entity
 * @returns {Promise<Object>} Entity data
 */
export const getEntity = fetchEntity;

/**
 * Fetch entities index for search functionality using modern Parquet index
 * Pure function for TanStack Query
 * @returns {Promise<Array>} Array of entities index with search metadata
 */
export const fetchEntitiesIndex = () => getEntitiesIndex();

/**
 * Clear all caches (useful for development or forced refresh)
 * @returns {void}
 */
export const clearEntityCaches = () => {
  parquetIndexCache = null;
  indexLoadingPromise = null;
};